import React, { useState } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { secureStorage } from '../../utils/secureStorage';
import toast from '../../utils/toast';
import { X, Save, TestTube } from 'lucide-react';
import { EMZI_COLORS, EMZI_RADIUS } from '../../styles/emziCI';
import { BACKEND_URL } from '../../utils/backendUrl';

const IntegrationEditModal = ({ integration, onClose, onSuccess }) => {
  const t = useLang();
  const [config, setConfig] = useState(integration.config || {});
  const [isActive, setIsActive] = useState(integration.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const integrationType = integration.integration_type;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/test`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ integration_type: integrationType, config })
        }
      );
      const result = await response.json();
      setTestResult(result);
      if (result.success) {
        toast.success(result.message || 'Test passed');
      } else {
        toast.error(result.message || 'Test failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Network error' });
      toast.error('Network error');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = secureStorage.getItem('nexus_access_token');

      const updatePayload = {
        config,
        is_active: isActive
      };

      // If test passed, also update status
      if (testResult?.success) {
        updatePayload.status = 'connected';
      }

      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/${integration.integration_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatePayload)
        }
      );

      if (response.ok) {
        toast.success(t('admin.integrations.updated') || 'Integration updated');
        onSuccess();
      } else {
        toast.error(t('admin.integrations.updateFailed') || 'Update failed');
      }
    } catch (error) {
      toast.error(t('admin.error.networkError') || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const renderConfigForm = () => {
    switch (integrationType) {
      case 'email_smtp':
        return (
          <div className="space-y-3">
            <FormField label="SMTP Host" value={config.host || ''} onChange={(v) => setConfig({ ...config, host: v })} placeholder="smtp.gmail.com" />
            <FormField label="Port" type="number" value={config.port || ''} onChange={(v) => setConfig({ ...config, port: parseInt(v) || '' })} placeholder="587" />
            <FormField label="Username" value={config.username || ''} onChange={(v) => setConfig({ ...config, username: v })} placeholder="admin@emzi.digital" />
            <FormField label="Password" type="password" value={config.password || ''} onChange={(v) => setConfig({ ...config, password: v })} placeholder="Enter password" />
          </div>
        );
      case 'whatsapp':
        return (
          <div className="space-y-3">
            <FormField label="API Token" type="password" value={config.api_token || ''} onChange={(v) => setConfig({ ...config, api_token: v })} placeholder="EAAx..." />
            <FormField label="Phone Number ID" value={config.phone_number_id || ''} onChange={(v) => setConfig({ ...config, phone_number_id: v })} placeholder="123456789" />
            <FormField label="Business Account ID" value={config.business_account_id || ''} onChange={(v) => setConfig({ ...config, business_account_id: v })} placeholder="987654321" />
          </div>
        );
      case 'telegram':
        return (
          <div className="space-y-3">
            <FormField label="Bot Token" type="password" value={config.bot_token || ''} onChange={(v) => setConfig({ ...config, bot_token: v })} placeholder="123456:ABC-DEF..." />
          </div>
        );
      case 'webhook':
        return (
          <div className="space-y-3">
            <FormField label="Webhook URL" type="url" value={config.url || ''} onChange={(v) => setConfig({ ...config, url: v })} placeholder="https://example.com/webhook" />
            <FormField label="Shared Secret" type="password" value={config.shared_secret || ''} onChange={(v) => setConfig({ ...config, shared_secret: v })} placeholder="Enter secret" />
          </div>
        );
      case 'email_sendgrid':
        return (
          <div className="space-y-3">
            <FormField label="API Key" type="password" value={config.api_key || ''} onChange={(v) => setConfig({ ...config, api_key: v })} placeholder="SG...." />
            <FormField label="Sender Email" value={config.sender_email || ''} onChange={(v) => setConfig({ ...config, sender_email: v })} placeholder="noreply@emzi.digital" />
          </div>
        );
      case 'email_gmail':
        return (
          <div className="space-y-3">
            <FormField label="Client ID" value={config.client_id || ''} onChange={(v) => setConfig({ ...config, client_id: v })} placeholder="xxx.apps.googleusercontent.com" />
            <FormField label="Client Secret" type="password" value={config.client_secret || ''} onChange={(v) => setConfig({ ...config, client_secret: v })} placeholder="Enter secret" />
          </div>
        );
      default:
        return <p className="text-sm text-gray-500">Unknown integration type</p>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="integration-edit-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">
                {t('admin.integrations.editTitle') || 'Edit Integration'}
              </h2>
              <p className="text-sm text-orange-100 mt-1">
                {integrationType.replace('_', ' ').toUpperCase()}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-edit-modal">
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Active toggle */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <span className="text-sm font-semibold text-gray-700">
              {t('admin.integrations.active') || 'Active'}
            </span>
            <button
              onClick={() => setIsActive(!isActive)}
              className="relative w-11 h-6 rounded-full transition-colors"
              style={{ backgroundColor: isActive ? EMZI_COLORS.success : '#D1D5DB' }}
              data-testid="integration-active-toggle"
            >
              <span
                className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                style={{ left: isActive ? '22px' : '2px' }}
              />
            </button>
          </div>

          {/* Config form */}
          {renderConfigForm()}

          {/* Test result */}
          {testResult && (
            <div className={`border-l-4 rounded-r-lg p-3 ${testResult.success ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
              <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.message}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex gap-2">
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
            data-testid="test-integration-btn"
          >
            {testing ? (
              <span className="animate-spin">
                <TestTube size={16} />
              </span>
            ) : (
              <TestTube size={16} />
            )}
            {testing ? 'Testing...' : 'Test'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: EMZI_COLORS.accent, borderRadius: EMZI_RADIUS.sm }}
            data-testid="save-integration-btn"
          >
            <Save size={16} />
            {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
      data-testid={`edit-field-${label.toLowerCase().replace(/\s+/g, '-')}`}
    />
  </div>
);

export default IntegrationEditModal;
